/**
 * reCAPTCHA Enterprise integration for Knox SPA.
 *
 * Provides functions to load the score-based reCAPTCHA script and execute
 * assessments with a specific action name. Tokens are sent to the backend
 * via the X-Recaptcha-Token header (injected by knoxRequest).
 */

/** Singleton promise to avoid loading the script multiple times. */
let loadPromise: Promise<void> | null = null;
/** The site key used to load the script. */
let loadedSiteKey: string | null = null;

/**
 * Dynamically load the reCAPTCHA Enterprise JS SDK.
 * Safe to call multiple times — subsequent calls resolve immediately if already loaded.
 *
 * @param siteKey - reCAPTCHA Enterprise score-based site key
 */
export async function loadRecaptchaScript(siteKey: string): Promise<void> {
  if (loadedSiteKey === siteKey && loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    // Check if already loaded (e.g. script was added externally)
    if (window.grecaptcha?.enterprise) {
      loadedSiteKey = siteKey;
      window.grecaptcha.enterprise.ready(() => resolve());
      return;
    }

    // Check if script tag already exists (avoid duplicates)
    const existing = document.querySelector(`script[src*="${siteKey}"]`);
    if (existing) {
      // Script is loading, wait for grecaptcha to be available
      const poll = setInterval(() => {
        if (window.grecaptcha?.enterprise) {
          clearInterval(poll);
          loadedSiteKey = siteKey;
          window.grecaptcha.enterprise.ready(() => resolve());
        }
      }, 50);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
    script.async = true;
    script.defer = true;

    script.onload = () => {
      window.grecaptcha!.enterprise!.ready(() => {
        loadedSiteKey = siteKey;
        resolve();
      });
    };

    script.onerror = () => {
      loadPromise = null; // Allow retry
      reject(new Error('Failed to load reCAPTCHA script'));
    };

    document.head.appendChild(script);
  });

  return loadPromise;
}

/**
 * Execute a reCAPTCHA assessment for the given action.
 * Must call loadRecaptchaScript() before this function.
 *
 * @param action - reCAPTCHA action name (e.g. "login", "register", "check_email")
 * @returns reCAPTCHA token string to be sent to the backend
 */
export async function executeRecaptcha(action: string): Promise<string> {
  if (!window.grecaptcha?.enterprise || !loadedSiteKey) {
    throw new Error('reCAPTCHA not loaded');
  }
  const token = await window.grecaptcha.enterprise.execute(loadedSiteKey, { action });
  return token;
}

// Extend Window interface for grecaptcha
declare global {
  interface Window {
    grecaptcha?: {
      enterprise?: {
        ready: (callback: () => void) => void;
        execute: (siteKey: string, options: { action: string }) => Promise<string>;
      };
      render?: (container: HTMLElement, options: Record<string, unknown>) => number;
      reset?: (widgetId?: number) => void;
    };
  }
}
